import { customFetchV2 } from "./fetch";

export interface MmsSharedTcrSummary {
  entryId?: string;
  label?: string;
  brandId?: string;
  campaignId?: string;
}

export interface MmsBlockerItem {
  code: string;
  message: string;
}

export type MmsPipelineStepState =
  | "complete"
  | "in_progress"
  | "pending"
  | "blocked"
  | "failed";

export interface MmsPipelineStepStatus {
  id: string;
  label: string;
  status: MmsPipelineStepState;
}

export interface MmsOnboardingStatusResponse {
  hasBrand: boolean;
  pendingBrandQueued: boolean;
  pendingCampaignQueued: boolean;
  pendingMessagingQueued: boolean;
  mmsStatus?: string;
  hasInfoAreaCode: boolean;
  useSharedTcr: boolean;
  sharedTcr?: MmsSharedTcrSummary;
  needsAttention?: boolean;
  pipelinePaused?: boolean;
  setupFailed?: boolean;
  setupErrorMessage?: string;
  blockers?: MmsBlockerItem[];
  steps?: MmsPipelineStepStatus[];
}

export async function getMmsOnboardingStatus(
  adminServiceUrl: string,
  charityId: string,
  firebaseToken: string
): Promise<MmsOnboardingStatusResponse> {
  const fullUrl = `${adminServiceUrl}/api/v1/${charityId}/mms/onboarding-status?token=${encodeURIComponent(
    firebaseToken
  )}`;

  return customFetchV2({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export interface MmsSharedTcrOption {
  entryId: string;
  label: string;
}

export async function getMmsSharedTcrCampaigns(
  adminServiceUrl: string,
  charityId: string,
  firebaseToken: string
): Promise<MmsSharedTcrOption[]> {
  const fullUrl = `${adminServiceUrl}/api/v1/${charityId}/mms/shared-tcr-campaigns?token=${encodeURIComponent(
    firebaseToken
  )}`;

  return customFetchV2({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function startMmsOnboarding(
  adminServiceUrl: string,
  charityId: string,
  firebaseToken: string
): Promise<string> {
  const fullUrl = `${adminServiceUrl}/api/v1/${charityId}/mms/start-onboarding?token=${encodeURIComponent(
    firebaseToken
  )}`;

  const response = await customFetchV2(
    {
      url: fullUrl,
      method: "POST",
      mode: "cors",
      headers: {
        "content-type": "application/json",
      },
    },
    false
  );

  return response.text();
}

export async function startMmsOnboardingWithSharedTcr(
  adminServiceUrl: string,
  charityId: string,
  firebaseToken: string,
  entryId: string
): Promise<string> {
  const fullUrl = `${adminServiceUrl}/api/v1/${charityId}/mms/start-with-shared-tcr?token=${encodeURIComponent(
    firebaseToken
  )}`;

  const response = await customFetchV2(
    {
      url: fullUrl,
      method: "POST",
      mode: "cors",
      body: JSON.stringify({ entryId }),
      headers: {
        "content-type": "application/json",
      },
    },
    false
  );

  return response.text();
}

export async function resumeMmsOnboarding(
  adminServiceUrl: string,
  charityId: string,
  firebaseToken: string
): Promise<string> {
  const fullUrl = `${adminServiceUrl}/api/v1/${charityId}/mms/resume-onboarding?token=${encodeURIComponent(
    firebaseToken
  )}`;

  const response = await customFetchV2(
    {
      url: fullUrl,
      method: "POST",
      mode: "cors",
      headers: {
        "content-type": "application/json",
      },
    },
    false
  );

  return response.text();
}

export async function cancelMmsOnboarding(
  adminServiceUrl: string,
  charityId: string,
  firebaseToken: string
): Promise<string> {
  const fullUrl = `${adminServiceUrl}/api/v1/${charityId}/mms/cancel-onboarding?token=${encodeURIComponent(
    firebaseToken
  )}`;

  const response = await customFetchV2(
    {
      url: fullUrl,
      method: "POST",
      mode: "cors",
      headers: {
        "content-type": "application/json",
      },
    },
    false
  );

  return response.text();
}

export async function syncMmsSenderAreaCode(
  adminServiceUrl: string,
  charityId: string,
  firebaseToken: string,
  areaCode: string
): Promise<string> {
  const fullUrl = `${adminServiceUrl}/api/v1/${charityId}/mms/sync-sender-area-code?token=${encodeURIComponent(
    firebaseToken
  )}`;

  const response = await customFetchV2(
    {
      url: fullUrl,
      method: "POST",
      mode: "cors",
      body: JSON.stringify({ areaCode }),
      headers: {
        "content-type": "application/json",
      },
    },
    false
  );

  return response.text();
}

export async function clearMmsSenderAreaCode(
  adminServiceUrl: string,
  charityId: string,
  firebaseToken: string
): Promise<string> {
  const fullUrl = `${adminServiceUrl}/api/v1/${charityId}/mms/sender-area-code?token=${encodeURIComponent(
    firebaseToken
  )}`;

  const response = await customFetchV2(
    {
      url: fullUrl,
      method: "DELETE",
      mode: "cors",
      headers: {
        "content-type": "application/json",
      },
    },
    false
  );

  return response.text();
}
